import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { TrainingModuleDto } from '../../../api/types/dtos/training';
import { useUpdateTrainingModule } from '../../../api/hooks/useTrainingModules';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { CloudUpload } from '@mui/icons-material';

interface EditModuleModalProps {
  open: boolean;
  onClose: () => void;
  module: TrainingModuleDto | null;
  trainingId: string;
}

const EditModuleModal: React.FC<EditModuleModalProps> = ({
  open,
  onClose,
  module,
  trainingId,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [skillName, setSkillName] = useState('');
  const [sequence, setSequence] = useState(1);
  const [progressContribution, setProgressContribution] = useState(100);
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(undefined);
  const [logoFileName, setLogoFileName] = useState('');
  const [logoRemoved, setLogoRemoved] = useState(false);
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const updateMutation = useUpdateTrainingModule(
    selectedOrganization?.id || '',
    trainingId
  );

  useEffect(() => {
    if (module && open) {
      setName(module.name);
      setDescription(module.description || '');
      setSkillName(module.skillName || '');
      setSequence(module.sequence);
      setProgressContribution(module.progressContribution);
      setLogoDataUrl(module.logo || undefined);
      setLogoFileName('');
      setLogoRemoved(false);
    }
  }, [module, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    if (!module?.id) {
      enqueueSnackbar('No se puede actualizar el módulo', { variant: 'error' });
      return;
    }

    if (progressContribution < 0 || progressContribution > 100) {
      enqueueSnackbar('La contribución al progreso debe estar entre 0 y 100', {
        variant: 'error',
      });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: module.id,
        data: {
          name: name.trim(),
          description: description.trim() || undefined,
          skillName: skillName.trim() || undefined,
          sequence,
          progressContribution,
          trainingId: module.trainingId,
          logo: logoRemoved ? null : logoDataUrl,
          isActive: module.isActive,
        },
      });

      enqueueSnackbar('Módulo actualizado exitosamente', { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar('Error al actualizar el módulo', { variant: 'error' });
      console.error('Error updating module:', error);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoDataUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
    setLogoFileName(file.name);
    setLogoRemoved(false);
  };

  const handleClearLogo = () => {
    setLogoDataUrl(undefined);
    setLogoFileName('');
    setLogoRemoved(true);
  };

  const hasLogoPreview = !!(logoDataUrl || (!logoRemoved && module?.logo));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Editar Módulo</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
          <TextField
            label="Nombre de Habilidad"
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
            fullWidth
          />
          <TextField
            label="Secuencia"
            type="number"
            value={sequence}
            onChange={(e) => setSequence(parseInt(e.target.value) || 1)}
            required
            fullWidth
            InputProps={{ 
              inputProps: { 
                min: 1,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
          <TextField
            label="Contribución al Progreso (%)"
            type="number"
            value={progressContribution}
            onChange={(e) =>
              setProgressContribution(parseInt(e.target.value) || 0)
            }
            required
            fullWidth
            helperText="La suma de todos los módulos debe ser 100%"
            InputProps={{ 
              inputProps: { 
                min: 0, 
                max: 100,
                onWheel: (e) => e.currentTarget.blur()
              } 
            }}
          />
          <Box>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              fullWidth
            >
              {logoFileName || logoDataUrl || (!logoRemoved && module?.logo)
                ? 'Cambiar logo'
                : 'Subir logo (opcional)'}
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleLogoUpload}
              />
            </Button>
            {hasLogoPreview && (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {logoFileName && (
                  <Typography variant="caption" color="text.secondary">
                    Logo seleccionado: <strong>{logoFileName}</strong>
                  </Typography>
                )}
                <Box
                  component="img"
                  src={logoDataUrl || module?.logo}
                  alt="Logo del módulo"
                  sx={{ maxHeight: 120, objectFit: 'contain', borderRadius: 1 }}
                />
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleClearLogo}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Quitar logo
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
          startIcon={updateMutation.isPending && <CircularProgress size={20} />}
        >
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditModuleModal;



